import { useEffect, type FC } from 'react';
import { useWikiIds } from './use-wiki-ids';
import { Icon } from '@flowus/common/icon-svg';
import { useTryCreatePersonalWiki } from './use-try-create-personal-wiki';
import { usePersonalWikiId } from './use-personal-wiki-id';
import { IconTrigger } from 'src/components/icon-trigger';
import { Images } from 'src/image';
import { segmentsToText } from 'src/editor/utils/editor';
import { cache, getState } from 'src/redux/store';
import { UNTITLED_WIKI } from 'src/common/const';
import { useFetchPage } from 'src/hooks/page';
import { useBlock } from 'src/hooks/block/use-block';
import { useGetPageId } from 'src/utils/getPageId';
import { request } from 'src/common/request';
import { findFirstAllowSubscribePage } from 'src/utils/block-utils';
import { addSubscribe } from '@flowus/common';
import { getCurrentUser, useCurrentUser } from 'src/hooks/user';
import { useIsNeedPay } from 'src/hooks/page/use-page-meta';
import { bizTracker } from 'src/utils/biz-tracker';
import { message } from '@flowus/common/components/message';
import { getUrlWithoutDomain } from 'src/common/utils/url-utils';
import { useSubscribeStatus } from 'src/hooks/wiki/store';
import { useOpenModal } from 'src/common/components/next-modal';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { i18n } from '@flowus/common/i18n';

interface Props {
  pageId: string;
  onSelectWiki: (wikiId: string) => void;
  onDimiss: () => void;
}
export const WikiList: FC<Props> = (props) => {
  const { t } = useAppTranslation();
  const wikiIds = useWikiIds();
  const personalWikiId = usePersonalWikiId();
  const block = useBlock(personalWikiId);
  const isNeedPay = useIsNeedPay(props.pageId);
  useTryCreatePersonalWiki();
  const currentUser = useCurrentUser();
  const openModal = useOpenModal();
  // 如果订阅过则个人知识库打勾
  const subscribed = useSubscribeStatus(props.pageId);
  const isSelected =
    subscribed ||
    !!block?.subNodes.find((v) => {
      return cache.blocks[v]?.data.ref?.uuid === props.pageId;
    });
  const isBlockHasWiki = !!block?.subNodes.find((v) => {
    return cache.blocks[v]?.data.ref?.uuid === props.pageId;
  });

  const fetchPage = useFetchPage();

  useEffect(() => {
    wikiIds.forEach((v) => {
      void fetchPage(v);
    });
  }, [fetchPage, wikiIds]);

  return (
    <div className="next-modal p-1">
      <div
        className="flex items-center justify-between animate-hover px-3 h-8"
        onClick={async () => {
          props.onDimiss();
          // 兼容订阅数据
          if (subscribed) {
            const targetId = findFirstAllowSubscribePage(props.pageId) ?? props.pageId;
            await addSubscribe(
              {
                targetId,
                subscribed: !subscribed,
                addSubscribe: request.infra.addSubscribe,
                cancelSubscribe: request.infra.cancelSubscribe,
                user: currentUser,
                callback: () => {},
              },
              {
                pageId: props.pageId,
                needPay: isNeedPay,
                userId: getCurrentUser().uuid,
                createdBy: block?.createdBy,
                spaceId: block?.spaceId,
                event: bizTracker.event,
              }
            );
            if (isBlockHasWiki) {
              personalWikiId && props.onSelectWiki(personalWikiId);
            } else {
              message.success(
                subscribed ? t('common.removed_from_wiki') : t('common.already_joined_wiki')
              );
            }
            return;
          }
          personalWikiId && props.onSelectWiki(personalWikiId);
        }}
      >
        <div className="space-x-1 flex items-center">
          <Icon name={'IcFavorite'} size="middle" />
          <div className="text-t2 text-normal text-ellipsis">{t('common.personal_wiki')}</div>
        </div>
        {isSelected && <Icon className="text-active_color" name={'IcSelectedSpace'} size="small" />}
      </div>
      {wikiIds.map((vId) => {
        return <Item key={vId} id={vId} onSelectWiki={props.onSelectWiki} />;
      })}
    </div>
  );
};

const Item: FC<{ id: string; onSelectWiki: (wikiId: string) => void }> = (props) => {
  const { id } = props;
  const pageId = useGetPageId();
  const block = useBlock(id);
  const isSelected = !!block?.subNodes.find((v) => {
    return cache.blocks[v]?.data.ref?.uuid === pageId;
  });
  return (
    <div
      key={id}
      className="flex items-center animate-hover px-3 h-8 justify-between"
      onClick={() => {
        id && props.onSelectWiki(id);
      }}
    >
      <div className="space-x-1 flex items-center overflow-hidden">
        <IconTrigger
          trigger={false}
          iconSize={20}
          blockId={id}
          defaultIcon={<img className="w-5 h-5" src={Images.wikiDefaultIcon} />}
        />
        <div className="text-t2 text-normal text-ellipsis w-[80%]">
          {segmentsToText(getState().blocks[id]?.data.segments) || UNTITLED_WIKI}
        </div>
      </div>
      {isSelected && <Icon className="text-active_color" name={'IcSelectedSpace'} size="small" />}
    </div>
  );
};
let tmpId: string;
export const removeWikiSuccessMessage = () => {
  tmpId && message.closeMessage(tmpId);
};
export const addWikiSuccess = (desc: string, targetId: string, key?: string) => {
  tmpId = message.success({
    key,
    duration: 10000,
    content: (
      <div className="flex items-center justify-between w-52">
        <div className="text-t2">{desc}</div>
        <div
          onClick={() => {
            window.open(`${getUrlWithoutDomain()}/wiki/${targetId}`);
            message.closeMessage(tmpId);
          }}
          className="text-t4 text-active_color animate-click"
        >
          {i18n.t('common.jump_to_view')}
        </div>
      </div>
    ),
  });
  return tmpId;
};
